import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "po_auth_token";
const USER_KEY = "po_auth_user";

export const authStore = {
  getToken: () => localStorage.getItem(TOKEN_KEY),
  getUser: () => {
    try {
      return JSON.parse(localStorage.getItem(USER_KEY) || "null");
    } catch {
      return null;
    }
  },
  save: (token, user) => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  },
  clear: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  },
};

export const api = axios.create({ baseURL: API });

// Inject Bearer token on every request
api.interceptors.request.use((config) => {
  const t = authStore.getToken();
  if (t) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

// Boot a lightweight global 401 handler that notifies the AuthContext.
// We dispatch a custom event so apiClient stays framework-free.
// Opt-out: pass `_skip401Logout: true` on a request config when 401 is a
// legitimate validation error (e.g. wrong current password, wrong confirmation
// password on delete-account, login attempts) and the user should stay signed in.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const skip = err?.config?._skip401Logout;
    if (err?.response?.status === 401 && !skip) {
      authStore.clear();
      window.dispatchEvent(new Event("po:auth-expired"));
    }
    return Promise.reject(err);
  },
);

/** Fetch a PDF (or any binary) as a Blob with the auth header, then open or save it. */
export async function openAuthedBlob(path, { openInNewTab = true, filename } = {}) {
  const token = authStore.getToken();
  const resp = await fetch(`${API}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!resp.ok) {
    throw new Error(`HTTP ${resp.status}`);
  }
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  if (filename) {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    return;
  }
  if (openInNewTab) {
    window.open(url, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    return;
  }
  return url;
}
